/**
 * Align Minhas Propostas (Comercial) with Quadro de Propostas: terminal SCD rows,
 * merged claim from API + marcação form_data.
 */

export function scdEntryIsTerminal(scd: unknown): boolean {
  if (!scd || typeof scd !== 'object') return false;
  const row = scd as Record<string, unknown>;
  const st = String(row.status || '').toLowerCase();
  if (st === 'cancelled' || row.cancelled_at) return true;
  if (st === 'completed' || row.completed_at) return true;
  return false;
}

export function scdEntryIsConcluidoNotCancelled(scd: unknown): boolean {
  if (!scd || typeof scd !== 'object') return false;
  const row = scd as Record<string, unknown>;
  if (row.cancelled_at || String(row.status || '').toLowerCase() === 'cancelled') return false;
  const st = String(row.status || '').toLowerCase();
  if (st === 'completed' || row.completed_at) return true;
  return false;
}

/** Every serviço has a terminal row in service_completion_data (same idea as BackOffice proposalAllServicesTerminal). */
export function allServicosHaveTerminalScd(servicos: unknown[], scdList: unknown[]): boolean {
  if (!Array.isArray(servicos) || servicos.length === 0) return false;
  const byIid: Record<string, unknown> = {};
  for (const scd of scdList || []) {
    if (!scd || typeof scd !== 'object') continue;
    const row = scd as Record<string, unknown>;
    const iid = row.instanceId ?? row.instance_id;
    if (iid != null && String(iid).trim()) byIid[String(iid).trim()] = scd;
  }
  for (const s of servicos) {
    if (!s || typeof s !== 'object') return false;
    const so = s as Record<string, unknown>;
    const iid = so.instanceId ?? so.instance_id;
    if (iid == null || !String(iid).trim()) return false;
    const scd = byIid[String(iid).trim()];
    if (!scd || !scdEntryIsTerminal(scd)) return false;
  }
  return true;
}

export function anyServicoConcluidoNotCancelled(servicos: unknown[], scdList: unknown[]): boolean {
  if (!Array.isArray(servicos) || servicos.length === 0) return false;
  const byIid: Record<string, unknown> = {};
  for (const scd of scdList || []) {
    if (!scd || typeof scd !== 'object') continue;
    const row = scd as Record<string, unknown>;
    const iid = row.instanceId ?? row.instance_id;
    if (iid != null && String(iid).trim()) byIid[String(iid).trim()] = scd;
  }
  for (const s of servicos) {
    if (!s || typeof s !== 'object') continue;
    const so = s as Record<string, unknown>;
    const iid = so.instanceId ?? so.instance_id;
    if (iid == null || !String(iid).trim()) continue;
    const scd = byIid[String(iid).trim()];
    if (scd && scdEntryIsConcluidoNotCancelled(scd)) return true;
  }
  return false;
}

export function latestTerminalAtFromScdList(scdList: unknown[]): string {
  let best = 0;
  let out = '';
  for (const scd of scdList || []) {
    if (!scd || typeof scd !== 'object') continue;
    const row = scd as Record<string, unknown>;
    for (const key of ['completed_at', 'cancelled_at'] as const) {
      const v = row[key];
      if (typeof v !== 'string' || !v) continue;
      const t = new Date(v).getTime();
      if (Number.isFinite(t) && t >= best) {
        best = t;
        out = v;
      }
    }
  }
  return out;
}

/**
 * Merge marcação claim with API claim: combine service_completion_data by instanceId;
 * top-level fields favour API (spread order).
 */
export function mergeProposalClaimsForComercial(
  apiClaim: Record<string, any> | null | undefined,
  marcacaoClaim: Record<string, any> | null | undefined
): Record<string, any> | null {
  const hasApi = apiClaim && typeof apiClaim === 'object';
  const hasMarc = marcacaoClaim && typeof marcacaoClaim === 'object';
  if (!hasApi && !hasMarc) return null;
  const a = hasApi ? { ...apiClaim! } : {};
  const b = hasMarc ? { ...marcacaoClaim! } : {};
  const aScd = Array.isArray(a.service_completion_data) ? a.service_completion_data : [];
  const bScd = Array.isArray(b.service_completion_data) ? b.service_completion_data : [];
  const byIid = new Map<string, any>();
  for (const row of bScd) {
    const id = row?.instanceId != null ? String(row.instanceId) : '';
    if (id) byIid.set(id, { ...row });
  }
  for (const row of aScd) {
    const id = row?.instanceId != null ? String(row.instanceId) : '';
    if (id) byIid.set(id, { ...(byIid.get(id) || {}), ...row });
  }
  const mergedScd = Array.from(byIid.values());
  const scdOut = mergedScd.length > 0 ? mergedScd : aScd.length > 0 ? aScd : bScd;
  return { ...b, ...a, service_completion_data: scdOut };
}
